// deps
import React, { memo, useCallback, useMemo } from "react";
// style
import {
    RewardsWidgetContainer,
    RewardsDineCashContainer,
    RewardLogo,
    RedeemableStyledText,
    UseRewardContainer,
    ApplyRewardContainer,
    EarnRewardsContainer,
    EarnedReward,
    RedeemableContainerWithArrow,
    TopArrowIconContainer,
} from "./styles";
// Constants
import { AMOUNT_FORMAT, REWARD_LOGO_HEIGHT_WIDTH } from "@dineout-cart-ui/constants/index";

// Interfaces
import { IRewardWidgetResponseData } from "@dineout-cart-ui/interfaces/Cart";

// Components
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { ToggleSwitch } from "@swiggy-private/core-ui";
import { ElementVisibilitySensor } from "@swiggy-private/dls-web-core/dist/components/ElementVisibilitySensor";
import { format } from "@swiggy-private/core-ui";
import ArrowRightIcon from "./ArrowRightIcon";
import TopArrowIcon from "./TopArrowIcon";
import { BoosterWidget } from "./BoosterWidget";
import { RewardTitleWithExtraInfo } from "./RewardTitleWithExtraInfo";
//helper
import { isRestaurantHaving2xDineCash } from "@dineout-cart-ui/helpers/index";
import { isEmpty } from "@swiggy-private/js-utils";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// Test IDs
import { REWARDS_WIDGET_TEST_IDS } from "@dineout-cart-ui/helpers/TestIds";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

const ARROW_ICON_DISABLED_COLOR = "rgba(255, 255, 255, 0.3)";
const ARROW_ICON_DEFAULT_COLOR = "#009062";

export interface IRewardsWidgetProps {
    rewardsWidget: IRewardWidgetResponseData;
    isSelected: boolean;
    isScenesFlow?: boolean;
    onToggleSwitch: () => void;
    // Analytics props
    eventId: string;
    dealCategory: string;
    restaurantId: number;
    billAmount: number;
    cashbackPerc: number;
    couponApplied?: boolean;
    couponDiscount?: number;
    onImpressionCashBackEarned: (props: {
        eventId: string;
        dealCategory: string;
        restaurantId: number;
        billAmount: number;
        cashbackToBeEarned: number;
        cashbackPerc: number;
        isRestaurantHaving2xDineCash?: boolean;
    }) => void;
    onImpressionCashBackToggle: (props: {
        eventId: string;
        dealCategory: string;
        restaurantId: number;
        billAmount: number;
        cashbackToSpend: number;
        cashbackToEarn: number;
        toggleState: string;
        remainingBalance: number;
        cashbackPerc: number;
        couponApplied: boolean;
        couponDiscount: number;
        isRestaurantHaving2xDineCash?: boolean;
    }) => void;
}

export const RewardsWidget: React.FC<IRewardsWidgetProps> = (props: IRewardsWidgetProps) => {
    const {
        rewardsWidget,
        isSelected,
        onToggleSwitch,
        isScenesFlow,
        // Analytics props
        eventId,
        dealCategory,
        restaurantId,
        billAmount,
        cashbackPerc,
        couponApplied = false,
        couponDiscount = 0,
        onImpressionCashBackEarned,
        onImpressionCashBackToggle,
    } = props;

    const {
        backgroundImage,
        earnedRewards,
        earnRewardsTitle,
        disableEarnRewardsTitle,
        logo,
        redeemableRewards,
        subtitle,
        title,
        extraInfoText,
        boosterBackgroundImage,
        boosterAnimation,
        header,
        isBooster,
    } = rewardsWidget;

    const isBoosterActive = useMemo(() => isRestaurantHaving2xDineCash(header, isBooster), [header, isBooster]);

    const handleCashBackEarnedImpression = useCallback(() => {
        onImpressionCashBackEarned({
            eventId,
            dealCategory,
            restaurantId,
            billAmount,
            cashbackToBeEarned: rewardsWidget.earnedRewards || 0,
            cashbackPerc,
            isRestaurantHaving2xDineCash: isBoosterActive,
        });
    }, [
        onImpressionCashBackEarned,
        eventId,
        dealCategory,
        restaurantId,
        billAmount,
        rewardsWidget.earnedRewards,
        cashbackPerc,
        isBoosterActive,
    ]);

    const handleCashBackToggleImpression = useCallback(() => {
        onImpressionCashBackToggle({
            eventId,
            dealCategory,
            restaurantId,
            billAmount,
            cashbackToSpend: rewardsWidget.redeemableRewards || 0,
            cashbackToEarn: rewardsWidget.earnedRewards || 0,
            toggleState: isSelected ? "on" : "off",
            remainingBalance: rewardsWidget.redeemableRewards || 0,
            cashbackPerc,
            couponApplied,
            couponDiscount,
            isRestaurantHaving2xDineCash: isBoosterActive,
        });
    }, [
        eventId,
        dealCategory,
        restaurantId,
        billAmount,
        isSelected,
        cashbackPerc,
        couponApplied,
        couponDiscount,
        onImpressionCashBackToggle,
        isBoosterActive,
        rewardsWidget.redeemableRewards,
        rewardsWidget.earnedRewards,
    ]);

    let toggleThumbColor: keyof IColors | undefined;
    if (isScenesFlow) {
        toggleThumbColor = isSelected ? "text_Button_Primary" : "text_disabled";
    }

    const renderRewardsContent = useCallback(
        () => (
            <RewardsWidgetContainer
                backgroundImage={isBoosterActive ? "" : backgroundImage}
                booster={isBoosterActive}
                data-testid={REWARDS_WIDGET_TEST_IDS.CONTAINER}
                isScenesFlow={isScenesFlow}
            >
                <ElementVisibilitySensor senseOnce onVisibilityChange={handleCashBackEarnedImpression}>
                    <EarnRewardsContainer isTitle={Boolean(title)}>
                        <Text
                            variant={title ? `V2.body3.neutral.medium` : `V2.body3.neutral.bold`}
                            color={disableEarnRewardsTitle ? "text_disabled" : "text_High_Emphasis"}
                            data-testid={REWARDS_WIDGET_TEST_IDS.EARNED_REWARDS_TITLE}
                        >
                            {earnRewardsTitle}
                        </Text>
                        <EarnedReward>
                            {isBoosterActive ? (
                                <TopArrowIcon />
                            ) : (
                                <ArrowRightIcon
                                    color={
                                        disableEarnRewardsTitle ? ARROW_ICON_DISABLED_COLOR : ARROW_ICON_DEFAULT_COLOR
                                    }
                                />
                            )}
                            <Text
                                variant="V2.body2.neutral.bold"
                                color={disableEarnRewardsTitle ? "text_disabled" : "text_Highest_Emphasis"}
                                data-testid={REWARDS_WIDGET_TEST_IDS.EARNED_REWARDS_AMOUNT}
                            >
                                {format(AMOUNT_FORMAT, earnedRewards?.toString() ?? "0")}
                            </Text>
                        </EarnedReward>
                    </EarnRewardsContainer>
                </ElementVisibilitySensor>
                <UseRewardContainer>
                    {!isEmpty(title) && (
                        <>
                            <RewardsDineCashContainer>
                                {!isBoosterActive && (
                                    <RewardLogo>
                                        <Image
                                            src={logo}
                                            height={REWARD_LOGO_HEIGHT_WIDTH}
                                            width={REWARD_LOGO_HEIGHT_WIDTH}
                                        />
                                    </RewardLogo>
                                )}
                                <RewardTitleWithExtraInfo
                                    title={title}
                                    subtitle={subtitle}
                                    extraInfoText={extraInfoText}
                                    isBoosterActive={isBoosterActive}
                                />
                            </RewardsDineCashContainer>
                            <ApplyRewardContainer>
                                <ElementVisibilitySensor senseOnce onVisibilityChange={handleCashBackToggleImpression}>
                                    <ToggleSwitch
                                        selected={isSelected}
                                        isDisabled={false}
                                        onClick={onToggleSwitch}
                                        activeColor="special_primary"
                                        thumbColor={toggleThumbColor}
                                        data-testid={REWARDS_WIDGET_TEST_IDS.TOGGLE_SWITCH}
                                    />
                                </ElementVisibilitySensor>
                                <RedeemableContainerWithArrow>
                                    {isBoosterActive && (
                                        <TopArrowIconContainer>
                                            <TopArrowIcon />
                                        </TopArrowIconContainer>
                                    )}
                                    <RedeemableStyledText
                                        variant="V2.body2.neutral.bold"
                                        color="text_Highest_Emphasis"
                                        data-testid={REWARDS_WIDGET_TEST_IDS.REDEEMABLE_REWARDS_AMOUNT}
                                    >
                                        {format(AMOUNT_FORMAT, redeemableRewards?.toString() ?? "0")}
                                    </RedeemableStyledText>
                                </RedeemableContainerWithArrow>
                            </ApplyRewardContainer>
                        </>
                    )}
                </UseRewardContainer>
            </RewardsWidgetContainer>
        ),
        [
            isBoosterActive,
            backgroundImage,
            isScenesFlow,
            handleCashBackEarnedImpression,
            title,
            disableEarnRewardsTitle,
            earnRewardsTitle,
            earnedRewards,
            logo,
            subtitle,
            handleCashBackToggleImpression,
            isSelected,
            onToggleSwitch,
            redeemableRewards,
            extraInfoText,
            toggleThumbColor,
        ],
    );
    return (
        <>
            {isBoosterActive ? (
                <BoosterWidget
                    backgroundImage={boosterBackgroundImage}
                    dinecash2xLogoUrl={header?.logo}
                    calloutIconUrl={header?.callout}
                    boosterAnimation={boosterAnimation}
                    useRewardTitle={title}
                    headerTitle={header?.title}
                    headerSubtitle={header?.subtitle}
                >
                    {renderRewardsContent()}
                </BoosterWidget>
            ) : (
                renderRewardsContent()
            )}
        </>
    );
};

export default memo(RewardsWidget);
